import { keyframes, styled } from "goober";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { useMouseTracking } from "../../hooks/useMouseTracking";
import { playSparkle } from "../../lib/sounds";
import logoAsleep from "./logo-asleep.svg";
import logoDead from "./logo-dead.svg";
import logo from "./logo.svg";

const Container = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const rotate = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const blink = keyframes`
  0%, 30% { opacity: 1; }
  50%, 80% { opacity: 0; }
  100% { opacity: 1; }
`;

const logoBlink = keyframes`
  0%, 90%, 100% { scale: 1 1; }
  95% { scale: 1 0.05; }
`;

// Position and fade are separate animations so the motion-path easing stays
// continuous (no per-keyframe stutter). offset-distance runs 0→100% over the
// first half, then holds while the z is invisible until its next float.
const floatMove = keyframes`
  0% { offset-distance: 0%; }
  50%, 100% { offset-distance: 100%; }
`;

const floatFade = keyframes`
  0% { opacity: 0; scale: 0.75; }
  15% { opacity: 1; }
  38% { opacity: 1; scale: 1; }
  50%, 100% { opacity: 0; scale: 1; }
`;

// Each z holds one building-ring color for a full float, snapping to the next
// at the 2.6s boundaries (when the z is invisible). Cycle length = 4 floats.
const zColorCycle = keyframes`
  0%, 25% { color: var(--pink-600); }
  25.01%, 50% { color: var(--sky-600); }
  50.01%, 75% { color: var(--orange-600); }
  75.01%, 100% { color: var(--teal-600); }
`;

const breathe = keyframes`
  0%, 100% { transform: translateY(1px); }
  50% { transform: translateY(-1px); }
`;

const GradientRing = styled("div")`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  box-sizing: border-box;
  padding: 1px;
  background: conic-gradient(
    from 90deg,
    var(--pink-600) 0deg 180deg,
    var(--sky-600) 180deg 270deg,
    var(--orange-600) 270deg 313.2deg,
    var(--teal-600) 313.2deg 360deg
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  animation: ${rotate} 5s linear infinite;
`;

const DashedGradientRing = styled("div")`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  box-sizing: border-box;
  padding: 1px;
  background: conic-gradient(
    from 90deg,
    var(--pink-600) 0deg 180deg,
    var(--sky-600) 180deg 270deg,
    var(--orange-600) 270deg 313.2deg,
    var(--teal-600) 313.2deg 360deg
  );
  -webkit-mask:
    repeating-conic-gradient(#fff 0deg 15deg, transparent 15deg 20deg),
    linear-gradient(#fff 0 0) content-box;
  -webkit-mask-composite: source-out;
  mask:
    repeating-conic-gradient(#fff 0deg 15deg, transparent 15deg 20deg),
    linear-gradient(#fff 0 0) content-box;
  mask-composite: subtract;
  animation: ${rotate} 3s linear infinite;
`;

const PlanDashedRing = styled(DashedGradientRing)`
  background: var(--purple-600);
`;

const RingFill = styled("div")`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: var(--cell-bg, var(--surface-primary));
`;

const Ring = styled("div")`
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  box-sizing: border-box;
  transform: translate(-50%, -50%);
`;

const LogoContainer = styled("div")`
  width: 16px;
  height: 16px;
  background: var(--control-bg);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  img {
    filter: brightness(0) invert(1);
    mix-blend-mode: difference;
  }
`;

const celebrate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const sparkleBurst = keyframes`
  0% {
    transform: translate(-50%, -50%) translate(0, 0) scale(0.2);
    opacity: 0;
  }
  15% { opacity: 1; }
  55% { opacity: 1; }
  100% {
    transform: translate(-50%, -50%) translate(var(--sx), var(--sy)) scale(1);
    opacity: 0;
  }
`;

const Sparkle = styled("span")`
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  pointer-events: none;
  animation: ${sparkleBurst} 0.55s ease-out both;
  z-index: 1;
`;

const sleepZBase = `
  position: absolute;
  top: -2px;
  font-weight: 700;
  line-height: 1;
  color: var(--pink-600);
  pointer-events: none;
  offset-anchor: 0 0;
  offset-rotate: 0deg;
`;

// A burst of three z's rises in quick succession (float delays 0/0.5/1.0s) then
// rests until the 3.0s cycle repeats. Each z's color is offset one step on the
// shared 12s cycle (delays 0/-3/-6s), so the trio is always three different
// colors and the burst rotates through the palette each cycle. Each rides its
// own offset-path — a smooth arc that curves right then up (◞).
const SleepZ1 = styled("span")`
  ${sleepZBase}
  left: 54%;
  offset-path: path("M 0 0 Q 4 -1.5 5.5 -9");
  animation:
    ${floatMove} 3s ease-out infinite backwards,
    ${floatFade} 3s ease-in-out infinite backwards,
    ${zColorCycle} 12s linear infinite;
`;

const SleepZ2 = styled("span")`
  ${sleepZBase}
  left: 62%;
  offset-path: path("M 0 0 Q 5.5 -2 7 -7");
  animation:
    ${floatMove} 3s ease-out 0.5s infinite backwards,
    ${floatFade} 3s ease-in-out 0.5s infinite backwards,
    ${zColorCycle} 12s linear -3s infinite;
`;

const SleepZ3 = styled("span")`
  ${sleepZBase}
  left: 58%;
  offset-path: path("M 0 0 Q 3 -1 4.5 -10");
  animation:
    ${floatMove} 3s ease-out 1s infinite backwards,
    ${floatFade} 3s ease-in-out 1s infinite backwards,
    ${zColorCycle} 12s linear -6s infinite;
`;

const sparkles = [
  {
    id: "a",
    color: "var(--pink-600)",
    size: 5,
    delay: 0,
    duration: 0.6,
    distance: 1.0,
    jitter: 0,
  },
  {
    id: "b",
    color: "var(--sky-600)",
    size: 4,
    delay: 70,
    duration: 0.55,
    distance: 1.2,
    jitter: 12,
  },
  {
    id: "c",
    color: "var(--orange-600)",
    size: 6,
    delay: 25,
    duration: 0.5,
    distance: 0.85,
    jitter: -8,
  },
  {
    id: "d",
    color: "var(--teal-600)",
    size: 3,
    delay: 110,
    duration: 0.55,
    distance: 1.05,
    jitter: 6,
  },
  {
    id: "e",
    color: "var(--purple-600)",
    size: 5,
    delay: 40,
    duration: 0.6,
    distance: 0.9,
    jitter: -14,
  },
  {
    id: "f",
    color: "var(--green-600)",
    size: 4,
    delay: 90,
    duration: 0.5,
    distance: 1.15,
    jitter: 10,
  },
  {
    id: "g",
    color: "var(--pink-600)",
    size: 3,
    delay: 10,
    duration: 0.55,
    distance: 0.95,
    jitter: -5,
  },
  {
    id: "h",
    color: "var(--sky-600)",
    size: 6,
    delay: 130,
    duration: 0.5,
    distance: 1.1,
    jitter: 15,
  },
];

const ringColors: Record<string, string> = {
  waiting: "var(--orange-600)",
  dragging: "var(--purple-600)",
  ready: "var(--green-600)",
  resting: "var(--gray-800)",
  error: "var(--red-600)",
};

type StatusIndicatorState =
  | "resting"
  | "sleeping"
  | "thinking"
  | "loading"
  | "building"
  | "waiting"
  | "dragging"
  | "ready"
  | "error";

type Props = {
  state: StatusIndicatorState;
  focused: boolean;
  size?: number;
  opaqueFill?: boolean;
  planMode?: boolean;
  celebrateTrigger?: number | null;
};

export function StatusIndicator({
  state,
  focused,
  size = 16,
  opaqueFill,
  planMode,
  celebrateTrigger,
}: Props) {
  const isSleeping = state === "sleeping";
  const isRestingUnfocused = state === "resting" && !focused;
  const logoOpacity = isRestingUnfocused || isSleeping ? 0.2 : 1;
  const showBorder = !isRestingUnfocused && !isSleeping;
  const isWaiting = state === "waiting" || state === "dragging";
  const isError = state === "error";
  const isBuilding = state === "building";
  const isDashedGradient = state === "thinking" || state === "loading";
  const isPlanResting = planMode && state === "resting";
  const isPlanning =
    planMode &&
    (state === "thinking" || state === "loading" || state === "building");
  const borderSize = size + 4;
  const logoSize = Math.round((size * 10) / 16);
  const ringColor = ringColors[state];
  const [celebrateKey, setCelebrateKey] = useState<number | null>(null);
  useEffect(() => {
    if (celebrateTrigger == null) return;
    // oxlint-disable-next-line react/react-compiler -- EffectSetState: mirrors the celebrate trigger and fires the sparkle sound
    setCelebrateKey(celebrateTrigger);
    playSparkle();
  }, [celebrateTrigger]);
  const sparkleDistance = size * 1.6;
  const logoRef = useRef<HTMLImageElement>(null);
  useMouseTracking(logoRef, isSleeping ? 0 : size * 0.06);
  const [blinkDelay] = useState(() => `-${Math.random() * 4}s`);
  return (
    <Container>
      {showBorder && opaqueFill && (
        <RingFill style={{ width: borderSize, height: borderSize }} />
      )}
      {showBorder &&
        (isPlanning ? (
          <PlanDashedRing style={{ width: borderSize, height: borderSize }} />
        ) : isBuilding ? (
          <GradientRing style={{ width: borderSize, height: borderSize }} />
        ) : isDashedGradient ? (
          <DashedGradientRing
            style={{ width: borderSize, height: borderSize }}
          />
        ) : (
          <Ring
            style={{
              width: borderSize,
              height: borderSize,
              borderWidth: 1,
              borderStyle: state === "resting" ? "dashed" : "solid",
              borderColor: isPlanResting ? "var(--purple-600)" : ringColor,
              ...(state === "resting" && {
                opacity: 0.5,
                animation: `${rotate} 5s linear infinite`,
              }),
              ...((isWaiting || isError) && {
                animation: `${blink} 0.8s ease-in-out infinite`,
              }),
            }}
          />
        ))}
      <LogoContainer
        style={{
          opacity: logoOpacity,
          width: size,
          height: size,
        }}
      >
        <img
          key={celebrateKey ?? "idle"}
          ref={logoRef}
          src={isSleeping ? logoAsleep : isError ? logoDead : logo}
          style={{
            width: logoSize,
            height: logoSize,
            animation:
              celebrateKey !== null
                ? `${celebrate} 0.7s cubic-bezier(0.45, 0, 0.55, 1)`
                : isSleeping
                  ? `${breathe} 3.8s ease-in-out infinite`
                  : isError
                    ? undefined
                    : `${logoBlink} 4s ease-in-out ${blinkDelay} infinite`,
          }}
          onAnimationEnd={() => setCelebrateKey(null)}
        />
        {celebrateKey !== null &&
          sparkles.map((sparkle, i) => {
            const angle =
              (i / sparkles.length) * Math.PI * 2 +
              (sparkle.jitter * Math.PI) / 180;
            const sx = Math.cos(angle) * sparkleDistance * sparkle.distance;
            const sy = Math.sin(angle) * sparkleDistance * sparkle.distance;
            return (
              <Sparkle
                key={`${celebrateKey}-${sparkle.id}`}
                style={
                  {
                    background: sparkle.color,
                    width: sparkle.size,
                    height: sparkle.size,
                    animationDelay: `${sparkle.delay}ms`,
                    animationDuration: `${sparkle.duration}s`,
                    "--sx": `${sx}px`,
                    "--sy": `${sy}px`,
                  } as CSSProperties
                }
              />
            );
          })}
      </LogoContainer>
      {isSleeping && (
        <>
          <SleepZ1 style={{ fontSize: 8 }}>z</SleepZ1>
          <SleepZ2 style={{ fontSize: 7 }}>z</SleepZ2>
          <SleepZ3 style={{ fontSize: 7 }}>z</SleepZ3>
        </>
      )}
    </Container>
  );
}
